<template>
  <div>
    <div class="calendar-container">
      <div>
        <div
          class="align-items-center justify-content-between flex-wrap !hidden md:flex"
        >
          <FullCalendarZoom v-model="zoom" />
          <div class="calendar-last-updated-at text-body">
            <b>Last time updated:</b> {{ lastUpdateLabel }}
          </div>
        </div>
        <FullCalendarTimeline
          ref="calendar"
          :options="fullCalendarOptions"
          :class-names="['orders-calendar', { 'mobile-calendar': isMobile }]"
          pre-work-enabled
          :zoom="zoom"
          @open-edit-modal="openModal"
          @refetch-events="refetchEvents"
          @open-create-appointment-modal="openCreateModal"
          @validation-error="showValidationError"
          @resize-appointment="handleResizeAppointment"
        />
      </div>
    </div>
    <span class="shortcut-label">Use shortcut Alt + R to reload data</span>
    <edit-appointment-modal
      v-if="editId"
      :appointment-id="editId"
      @hide-modal="closeModal"
    ></edit-appointment-modal>
    <appointment-create-modal
      v-if="showCreateModal"
      :scheduled_time="createModalData.scheduled_time"
      :technician_id="createModalData.technician_id"
      @hide-modal="closeCreateModal"
      @created="closeCreateModal"
    ></appointment-create-modal>
    <pinned-reason-modal
      v-if="pinToTechId"
      :technician-name="pinToTechName"
      :technician-id="pinToTechId"
      :appointment-id="pinApptId"
      :pending-time-payload="pendingTimePayload"
      :multi-tech-ids="pendingMultiTechIds"
      @hide-modal="handlePinnedModalClose"
    ></pinned-reason-modal>
    <reschedule-notify-modal
      v-if="pendingReschedulePayload"
      :pending-payload="pendingReschedulePayload"
      @hide-modal="handleRescheduleModalClose"
    />
    <multi-tech-unassign-modal
      v-if="pendingMultiTechUnassign"
      :pending-payload="pendingMultiTechUnassign"
      :removed-tech-name="pendingMultiTechUnassign.removedTechName"
      @hide-modal="handleMultiTechUnassignModalClose"
    />
  </div>
</template>

<script>
import moment from "moment-timezone";
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { isMobileScreenSize } from "@/lib/helpers.js";
import { getQueryVariables } from "@/lib/url.js";

import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import CONSTANTS from "@/Constants";
import EditAppointmentModal from "@/EditAppointmentModal/EditAppointmentModal.vue";
import { currentTenantTimezone } from "../lib/helpers.js";
import FullCalendarTimeline from "./FullCalendarTimeline.vue";
import FullCalendarZoom from "./FullCalendarZoom.vue";
import MultiTechUnassignModal from "./MultiTechUnassignModal.vue";
import PinnedReasonModal from "./PinnedReasonModal.vue";
import RescheduleNotifyModal from "./RescheduleNotifyModal.vue";
const { SCHEDULE_DEFAULT_ZOOM } = CONSTANTS;

const PLANNER_REFETCH_DELAY = 5 * 1000;
const CALENDAR_REFRESH_INTERVAL = 60 * 1000;

export default {
  components: {
    FullCalendarTimeline,
    EditAppointmentModal,
    AppointmentCreateModal,
    MultiTechUnassignModal,
    PinnedReasonModal,
    RescheduleNotifyModal,
    FullCalendarZoom
  },
  data() {
    return {
      calendar: null,
      timezone: currentTenantTimezone,
      updatedAt: new Date(),
      plannerTimeout: null,
      calendarInterval: null,
      isUnmounted: false,
      editId: null,
      showCreateModal: false,
      createModalData: {
        scheduled_time: "",
        technician_id: null
      },
      pinToTechId: null,
      pinApptId: null,
      pinToTechName: "",
      pendingTimePayload: null,
      pendingReschedulePayload: null,
      pendingMultiTechIds: null,
      pendingMultiTechUnassign: null,
      zoom: SCHEDULE_DEFAULT_ZOOM
    };
  },
  computed: {
    ...mapState(["tenant", "fetchingTenant"]),
    ...mapGetters(["featureEnabledForTenant"]),
    ...mapState({
      department_ids({ scheduleBoardFilters }) {
        return scheduleBoardFilters.department_ids;
      },
      start({ scheduleBoardFilters }) {
        return scheduleBoardFilters.start;
      },
      fullCalendarOptions() {
        const { start } = getQueryVariables();
        // Pull cached filters
        let cachedFilters = localStorage.getItem("scheduleBoardFilters");
        cachedFilters = cachedFilters ? JSON.parse(cachedFilters) : {};
        const initialDate = start || cachedFilters.start || new Date();
        return {
          initialDate,
          timeZone: this.timezone,
          resources: $.debounce(100, (fetchInfo, successCallback) => {
            return this.fetchData(
              successCallback,
              fetchInfo.start,
              fetchInfo.end,
              "appointments_calendar/resources.json"
            );
          }),
          events: $.debounce(50, (fetchInfo, successCallback) => {
            const wrappedCb = this.isMultiTechEnabled
              ? (data) => successCallback(this.expandMultiTechEvents(data))
              : successCallback;
            return this.fetchData(
              wrappedCb,
              fetchInfo.start,
              fetchInfo.end,
              "appointments_calendar/events.json",
              "&status=all",
              "events"
            );
          }),
          eventAllow: (_dropInfo, draggedEvent) => {
            return !this.isEventDragLocked(draggedEvent);
          },
          eventDrop: this.handleEventDrop
        };
      },
      lastUpdateLabel() {
        return moment(this.updatedAt).format("h:mm:ss a");
      },
      isMobile() {
        return isMobileScreenSize();
      },
      isMultiTechEnabled() {
        return this.featureEnabledForTenant("multiple_technicians_ff_enabled");
      }
    })
  },
  watch: {
    department_ids(newValue, oldValue) {
      if (newValue !== oldValue) {
        const api = this.getCalendarApi();
        api?.refetchResources?.();
        this.refetchEvents();
      }
    }
  },
  created() {
    if (!this.tenant && !this.fetchingTenant) {
      this.fetchTenant();
    }
  },
  mounted() {
    const { appointment_id } = getQueryVariables();
    if (appointment_id) {
      this.openModal(appointment_id);
    }
    this.pollData();
    this.refetchEvents = this.refetchEvents.bind(this);
    document.addEventListener("keydown", this.handleKeyboardShortcut);
    EventBus.$on("refresh-schedule-board", this.refetchEvents);
    EventBus.$on("schedule-board-date-change", this.handleDateChange);
  },
  beforeUnmount() {
    this.isUnmounted = true;
    clearTimeout(this.plannerTimeout);
    clearInterval(this.calendarInterval);
    document.removeEventListener("keydown", this.handleKeyboardShortcut);
    EventBus.$off("refresh-schedule-board", this.refetchEvents);
    EventBus.$off("schedule-board-date-change", this.handleDateChange);
  },
  methods: {
    ...mapActions([
      "fetchTenant",
      "updateAppointmentEvent",
      "assignAppointmentEvent"
    ]),
    getCalendarApi() {
      if (this.isUnmounted) return null;
      const calendar = this.$refs.calendar;
      if (!calendar || !calendar.api) return null;
      return calendar.api;
    },
    handleDateChange(date) {
      const api = this.getCalendarApi();
      if (api?.gotoDate) {
        api.gotoDate(date);
      }
    },
    handleKeyboardShortcut(e) {
      // Alt + r to refetch events
      if (e.altKey && e.which === 82) {
        this.refetchEvents();
      }
    },
    fetchData(callback, start, end, url, additionalParams, resultKey) {
      const { department_ids } = getQueryVariables();
      let queryParams = `?start=${moment(start).format()}&end=${moment(
        end
      ).format()}&_=${Date.now()}`;
      if (department_ids) {
        queryParams = `${queryParams}&department_ids=${department_ids}`;
      }
      if (additionalParams) {
        queryParams += additionalParams;
      }
      return $.ajax("" + url + queryParams).then((res) => {
        if (this.isUnmounted) return;
        this.updatedAt = new Date();
        if (callback) {
          const data = resultKey ? res[resultKey] : res;
          const normalizedData =
            resultKey === "events" && Array.isArray(data)
              ? this.lockNonPendingEvents(data)
              : data;
          return callback(normalizedData);
        }
      });
    },
    eventStatus(event) {
      const props = event?.extendedProps || {};
      const status = props.status || event?.status;
      if (typeof status === "string") return status.toLowerCase();
      return status;
    },
    isEventDragLocked(event) {
      const props = event?.extendedProps || {};

      if (props.calendar_event) return true;
      // Travel/pre-work rows represent non-pending appointment stages and should never be dragged.
      if (props.travel || props.pre_work) return true;
      // Multi-tech clones lock per-tech: a still-assigned tech stays draggable
      // for reassignment even when a crewmate is running.
      if (props._isMultiTechClone) return props._techStatus !== "assigned";
      const status = this.eventStatus(event);
      if (status && status !== "pending") return true;
      return false;
    },
    lockNonPendingEvents(events) {
      return events.map((event) => {
        if (!this.isEventDragLocked(event)) return event;

        return {
          ...event,
          editable: false,
          startEditable: false,
          durationEditable: false,
          resourceEditable: false
        };
      });
    },
    expandMultiTechEvents(events) {
      const expanded = [];
      for (const event of events) {
        const techIds = event.appointment_technician_ids;
        if (techIds && techIds.length > 1) {
          const techStatuses = event.technician_statuses || {};
          const isTravelEvent = event.travel === true;
          const isPreworkEvent = event.pre_work === true;
          for (const techId of techIds) {
            const techData = techStatuses[techId];
            if (isTravelEvent) {
              if (!techData?.dispatch_time) continue;
              expanded.push(this.buildTravelClone(event, techId, techData));
            } else if (isPreworkEvent) {
              if (!techData?.accepted_time) continue;
              expanded.push(this.buildPreworkClone(event, techId, techData));
            } else {
              expanded.push(
                this.buildAppointmentClone(event, techId, techData, techIds)
              );
            }
          }
        } else {
          expanded.push(event);
        }
      }
      return expanded;
    },
    buildAppointmentClone(event, techId, techData, techIds) {
      const techStatus = techData?.technician_status;
      // Per-tech draggability: a still-assigned tech can be reassigned to
      // another row even when a crewmate is already running. The time/window
      // stays locked unless the whole appointment is still pending, because all
      // techs share one scheduled_time and a running crewmate already started.
      const perTechAssigned = techStatus === "assigned";
      const apptPending = event.status === "pending";
      const clone = {
        ...event,
        id: `${event.id}_mt_${techId}`,
        resourceId: techId,
        color: techData?.color || event.color,
        editable: perTechAssigned,
        resourceEditable: perTechAssigned,
        startEditable: perTechAssigned && apptPending,
        durationEditable: perTechAssigned && apptPending,
        extendedProps: {
          ...event.extendedProps,
          _appointmentId: event.id,
          _allTechIds: techIds,
          _isMultiTechClone: true,
          _techStatus: techStatus
        }
      };
      if (techData) {
        this.applyPerTechTimes(clone, techData);
      }
      return clone;
    },
    buildTravelClone(event, techId, techData) {
      return {
        ...event,
        id: `${event.id}_mt_${techId}`,
        resourceId: techId,
        start: techData.dispatch_time,
        end: techData.arrival_time || new Date().toISOString(),
        extendedProps: {
          ...event.extendedProps,
          _isMultiTechClone: true
        }
      };
    },
    buildPreworkClone(event, techId, techData) {
      return {
        ...event,
        id: `${event.id}_mt_${techId}`,
        resourceId: techId,
        start: techData.accepted_time,
        end: techData.dispatch_time || new Date().toISOString(),
        extendedProps: {
          ...event.extendedProps,
          _isMultiTechClone: true
        }
      };
    },
    applyPerTechTimes(clone, techData) {
      const status = techData.technician_status;
      if (
        (status === "arrived" || status === "departed") &&
        techData.arrival_time
      ) {
        clone.start = techData.arrival_time;
      }
      if (status === "departed" && techData.departure_time) {
        clone.end = techData.departure_time;
      }
      // Ensure minimum visual duration
      if (clone.start && clone.end) {
        const startMs = new Date(clone.start).getTime();
        const endMs = new Date(clone.end).getTime();
        const minDuration = 15 * 60 * 1000;
        if (endMs - startMs < minDuration) {
          clone.end = new Date(startMs + minDuration).toISOString();
        }
      }
    },
    handleEventDrop(info) {
      // Hide all tooltips
      tippy.hideAll();
      const oldResource = info.oldResource;
      const newResource = info.newResource;
      const isMultiTech = info.event.extendedProps?._isMultiTechClone === true;
      const appointmentId = this.extractAppointmentId(info.event.id);
      if (this.isEventDragLocked(info.event)) {
        info.revert?.();
        return;
      }

      // A multi-tech clone past pending can be reassigned but not rescheduled
      // (one shared scheduled_time, and a crewmate has already started). Ignore
      // any time delta so we never POST a reschedule the backend rejects with a
      // 409 — this holds even if a drag slips past the per-tech startEditable lock.
      const timeReschedulable =
        !isMultiTech || this.eventStatus(info.event) === "pending";
      const timeChanged =
        timeReschedulable &&
        info.event.start?.getTime() !== info.oldEvent.start?.getTime();

      let timePayload = null;
      if (timeChanged) {
        const newStart = moment(info.event.start);
        const newEnd = moment(info.event.end);
        timePayload = {
          arrival_window_start: newStart.format(),
          arrival_window_end: newEnd.format(),
          scheduled_time: newStart.clone().add(1, "hour").format()
        };
      }

      // Ignore null resources
      if (oldResource && newResource) {
        // Safely check for unassigned - department.id will be null/undefined for unassigned row
        const newDept = newResource.extendedProps?.department;
        const toUnassigned = !newDept?.id;
        const technicianChanged = oldResource.id !== newResource.id;

        if (technicianChanged && isMultiTech) {
          const allTechIds = info.event.extendedProps._allTechIds;
          const sourceTechId = parseInt(oldResource.id, 10);
          const targetTechId = toUnassigned
            ? null
            : parseInt(newResource.id, 10);

          // Build new roster: remove source tech, add target tech (if not already present)
          const newRoster = allTechIds.filter((id) => id !== sourceTechId);
          if (targetTechId && !newRoster.includes(targetTechId)) {
            newRoster.push(targetTechId);
          }

          if (toUnassigned) {
            this.pendingMultiTechUnassign = {
              appointmentId,
              technicianProfileIds: newRoster,
              timePayload,
              removedTechName: oldResource.title
            };
          } else {
            // Show pinned reason modal for multi-tech swap
            this.pinToTechName = newResource.title;
            this.pinToTechId = targetTechId;
            this.pinApptId = appointmentId;
            this.pendingTimePayload = timePayload;
            this.pendingMultiTechIds = newRoster;
          }
        } else if (technicianChanged) {
          // Use null for unassigned, otherwise parse the tech ID
          const newTechId = toUnassigned ? null : parseInt(newResource.id, 10);
          const newTechName = newResource.title;

          if (toUnassigned) {
            this.pendingReschedulePayload = {
              appointmentId,
              technician_profile_id: newTechId,
              ...timePayload
            };
          } else {
            // Show pinned reason modal for assigning to a technician
            this.pinToTechName = newTechName;
            this.pinToTechId = newTechId;
            this.pinApptId = appointmentId;
            this.pendingTimePayload = timePayload;
          }
        } else if (timeChanged) {
          this.pendingReschedulePayload = {
            appointmentId,
            ...timePayload
          };
        } else {
          // No actual change - reset UI
          this.refetchEvents();
        }
      } else if (timeChanged) {
        this.pendingReschedulePayload = {
          appointmentId,
          ...timePayload
        };
      }
    },
    refetchEvents() {
      const api = this.getCalendarApi();
      if (!api?.refetchEvents) return;
      api.refetchEvents();
      this.updatedAt = new Date();
    },
    waitForPlannerUpdates() {
      // Refetch events after planner updates
      clearTimeout(this.plannerTimeout);
      this.plannerTimeout = setTimeout(
        () => this.refetchEvents(),
        PLANNER_REFETCH_DELAY
      );
    },
    extractAppointmentId(id) {
      const rawId = String(id);
      return rawId.includes("_mt_")
        ? parseInt(rawId.split("_mt_")[0], 10)
        : parseInt(rawId, 10);
    },
    openModal(id) {
      try {
        const appointmentId = this.extractAppointmentId(id);
        EventBus.$emit("open-appointment-drawer", appointmentId);
      } catch (e) {
        console.error(e);
      }
    },
    closeModal() {
      this.editId = null;
      this.refetchEvents();
    },
    openCreateModal(data) {
      this.createModalData = {
        scheduled_time: data.scheduled_time,
        technician_id: data.technician_id
      };
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.createModalData = {
        scheduled_time: "",
        technician_id: null
      };
      this.refetchEvents();
    },
    pollData() {
      clearInterval(this.calendarInterval);
      this.calendarInterval = setInterval(() => {
        this.refetchEvents();
      }, CALENDAR_REFRESH_INTERVAL);
    },
    handlePinnedModalClose() {
      this.pinToTechName = null;
      this.pinToTechId = null;
      this.pinApptId = null;
      this.pendingTimePayload = null;
      this.pendingMultiTechIds = null;
      this.refetchEvents();
    },
    handleResizeAppointment(payload) {
      return this.updateAppointmentEvent(payload).then(() => {
        window.notify.success("Appointment updated");
        this.refetchEvents();
      });
    },
    handleRescheduleModalClose() {
      this.pendingReschedulePayload = null;
      this.refetchEvents();
    },
    handleMultiTechUnassignModalClose() {
      this.pendingMultiTechUnassign = null;
      this.refetchEvents();
    },
    showValidationError(message) {
      // Show validation error message using the same pattern as scheduler 2.0
      window.notify.error(message);
    }
  }
};
</script>

<style lang="scss" scoped>
.shortcut-label {
  font-style: italic;
  margin-top: 10px;
  color: var(--gray);
  font-size: 0.8em;
  display: inline-block;
  position: absolute;
  bottom: -20px;
}
.card {
  padding: 15px;
}

.calendar-container {
  position: relative;
  padding-top: 0 !important;
  margin-top: -20px;
  .calendar-last-updated-at {
    color: var(--font-body-color);
    font-size: 0.8em;
    height: 28px;
  }
}

@media (max-width: 600px) {
  .calendar-last-updated-at {
    left: 18px;
    top: 0px !important;
    font-size: 0.8em !important;
  }
}
</style>
